import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { Observable } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Component, OnInit } from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'
import { PayMethodType } from '../../sale/payment/payment'
import { ConfigOptionsKeys } from '../../../shared/constants'
import { getEnumAsArray } from '../../../shared/until'
import type { CheckoutPaymentBrand } from '../configuration-option'
import { configurationOptionSelectors } from '../configuration-option.selectors'
import { ConfigurationOptionService } from '../configuration-option.service'
import { NotificationService } from 'src/app/core/notification.service'

const paymentBrandOptions: { id: CheckoutPaymentBrand; label: string }[] = [
  { id: 'visa', label: 'Visa' },
  { id: 'mastercard', label: 'MasterCard' },
  { id: 'maestro', label: 'Maestro' },
  { id: 'sepa', label: 'Sepa' },
  { id: 'ideal', label: 'Ideal' },
  { id: 'apple-pay', label: 'Apple Pay' },
  { id: 'google-pay', label: 'Google Pay' },
  { id: 'paypal', label: 'PayPal' },
  { id: 'bancontact', label: 'Bancontact' }
]
const activeAllowedPayMethodOptions = getEnumAsArray(PayMethodType).filter(
  (payMethodOption) =>
    payMethodOption.value !== PayMethodType.PAY_METHOD_UNDECIDED
)
const activeAllowedPayMethodValues = activeAllowedPayMethodOptions.map(
  (payMethodOption) => payMethodOption.value
)
const paymentBrandIds = paymentBrandOptions.map(
  (paymentBrandOption) => paymentBrandOption.id
)

@Component({
  selector: 'configuration-option-payments-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class ConfigurationOptionPaymentsTabComponent implements OnInit {
  public configFormValues$: Observable<any>
  public isLoading$: Observable<boolean>
  public isOgoneCollapsed = false
  public isAllowedPaymentMethodsCollapsed = false
  public isPaymentBrandsCollapsed = false

  public form: UntypedFormGroup
  public accessRights = AccessRights
  public configOptionKeys = ConfigOptionsKeys
  public activeAllowedPayMethodOptions = activeAllowedPayMethodOptions
  public paymentBrandOptions = paymentBrandOptions
  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private configOptionService: ConfigurationOptionService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      ogoneurl: ['', []],
      sales_person: ['', [Validators.min(0)]],
      [ConfigOptionsKeys.ACTIVE_ALLOWED_PAY_METHODS]: [[]],
      [ConfigOptionsKeys.CHECKOUT_PAYMENT_BRANDS]: [[]]
    })

    this.configFormValues$ = this.store
      .select(configurationOptionSelectors.selectAll)
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        map((configOptions) => {
          const formValues =
            this.configOptionService.mapEntitiesToObject(configOptions)
          const activeAllowedPayMethods = JSON.parse(
            formValues[ConfigOptionsKeys.ACTIVE_ALLOWED_PAY_METHODS] || '[]'
          )
          const checkoutPaymentBrands = JSON.parse(
            formValues[ConfigOptionsKeys.CHECKOUT_PAYMENT_BRANDS] || '[]'
          )

          return {
            ...formValues,
            [ConfigOptionsKeys.ACTIVE_ALLOWED_PAY_METHODS]:
              activeAllowedPayMethodValues.filter((payMethod) =>
                activeAllowedPayMethods.includes(payMethod)
              ),
            [ConfigOptionsKeys.CHECKOUT_PAYMENT_BRANDS]: paymentBrandIds.filter(
              (paymentBrandId) => checkoutPaymentBrands.includes(paymentBrandId)
            )
          }
        })
      )

    this.isLoading$ = this.store.select(
      configurationOptionSelectors.selectIsLoading
    )
  }

  public onSubmit(configOptionKeys: string[]): void {
    const values = configOptionKeys.reduce<Record<string, unknown>>(
      (partialValues, key) => {
        partialValues[key] = this.form.value[key]
        return partialValues
      },
      {}
    )

    this.configOptionService.updateOrCreateMany(values)

    this.notificationService.emit('alerts.success', 'success')
  }

  public toggleOgoneCollapsed(): void {
    this.isOgoneCollapsed = !this.isOgoneCollapsed
  }

  public onActiveAllowedPayMethodChange(
    payMethod: PayMethodType,
    event: Event
  ): void {
    const selectedPayMethods = new Set<PayMethodType>(
      this.form.value[ConfigOptionsKeys.ACTIVE_ALLOWED_PAY_METHODS]
    )
    const isChecked = (event.target as HTMLInputElement).checked
    if (isChecked) {
      selectedPayMethods.add(payMethod)
    } else {
      selectedPayMethods.delete(payMethod)
    }

    this.form.controls[ConfigOptionsKeys.ACTIVE_ALLOWED_PAY_METHODS].setValue(
      activeAllowedPayMethodValues.filter((payMethodValue) =>
        selectedPayMethods.has(payMethodValue)
      )
    )
    this.form.controls[
      ConfigOptionsKeys.ACTIVE_ALLOWED_PAY_METHODS
    ].markAsDirty()
  }

  public onPaymentBrandChange(
    brandId: CheckoutPaymentBrand,
    event: Event
  ): void {
    const selectedBrandIds = new Set<CheckoutPaymentBrand>(
      this.form.value[ConfigOptionsKeys.CHECKOUT_PAYMENT_BRANDS]
    )
    const isChecked = (event.target as HTMLInputElement).checked
    if (isChecked) {
      selectedBrandIds.add(brandId)
    } else {
      selectedBrandIds.delete(brandId)
    }

    this.form.controls[ConfigOptionsKeys.CHECKOUT_PAYMENT_BRANDS].setValue(
      paymentBrandIds.filter((paymentBrandId) =>
        selectedBrandIds.has(paymentBrandId)
      )
    )
    this.form.controls[ConfigOptionsKeys.CHECKOUT_PAYMENT_BRANDS].markAsDirty()
  }
}
